import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { buildCustomerRoute, isSubdomainStoreMode } from '../../utils/storeUtils';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const UserType = {
  AppAdmin: 1,
  TeamMember: 2,
  Customer: 3
};

export default function UserTypeProtectedRoute({ 
  children, 
  allowedUserTypes = [],
  redirectTo 
}) {
  const { 
    isManagementAuthenticated, managementUser, managementLoading,
    isCustomerAuthenticated, customerUser, customerLoading
  } = useAuth();
  const location = useLocation();
  const { storeId } = useParams();

  // Determine if this route is for customer or management based on allowedUserTypes
  const isCustomerRoute = allowedUserTypes.includes(UserType.Customer) && !allowedUserTypes.includes(UserType.AppAdmin) && !allowedUserTypes.includes(UserType.TeamMember);
  const isManagementRoute = (allowedUserTypes.includes(UserType.AppAdmin) || allowedUserTypes.includes(UserType.TeamMember)) && !allowedUserTypes.includes(UserType.Customer);

  // Pick the correct auth state
  const isAuthenticated = isCustomerRoute ? isCustomerAuthenticated : 
                          isManagementRoute ? isManagementAuthenticated :
                          (isCustomerAuthenticated || isManagementAuthenticated);
  const user = isCustomerRoute ? customerUser : 
               isManagementRoute ? managementUser :
               (customerUser || managementUser);
  const loading = isCustomerRoute ? customerLoading : 
                  isManagementRoute ? managementLoading :
                  (managementLoading || customerLoading);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Determine default redirect based on allowed user types if not provided
  let defaultRedirect = redirectTo;
  if (!defaultRedirect) {
    if (allowedUserTypes.includes(3)) {
      if (isSubdomainStoreMode()) {
        // storeId is implied by the host; login lives at the root.
        defaultRedirect = buildCustomerRoute(null, 'login');
      } else if (storeId) {
        // For customer routes, use store-specific login if storeId is available
        defaultRedirect = buildCustomerRoute(storeId, 'login');
      } else {
        // Try to get from localStorage or current path
        const pathStoreId = location.pathname.match(/^\/stores\/([^/]+)/)?.[1];
        const storedStoreId = localStorage.getItem('currentStoreId');
        const currentStoreId = pathStoreId || storedStoreId;
        defaultRedirect = currentStoreId 
          ? buildCustomerRoute(currentStoreId, 'login')
          : '/not-found';
      }
    } else if (allowedUserTypes.includes(1) || allowedUserTypes.includes(2)) {
      defaultRedirect = '/management/team-member/login'; // Management
    } else {
      defaultRedirect = '/login'; // Fallback
    }
  }

  if (!isAuthenticated) {
    // Preserve the full pathname as redirect parameter
    const redirectParam = `?redirect=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={`${defaultRedirect}${redirectParam}`} replace />;
  }

  if (!user || !user.userType) {
    return <Navigate to={defaultRedirect} replace />;
  }

  const userTypeNumber = typeof user.userType === 'number' 
    ? user.userType 
    : UserType[user.userType] || parseInt(user.userType);

  const isAllowed = allowedUserTypes.length === 0 || allowedUserTypes.includes(userTypeNumber);

  if (!isAllowed) {
    if (userTypeNumber === UserType.Customer) {
      // Redirect to store overview if storeId is available
      const pathStoreId = location.pathname.match(/^\/stores\/([^/]+)/)?.[1];
      const storedStoreId = localStorage.getItem('currentStoreId');
      const currentStoreId = pathStoreId || storedStoreId;
      const redirectPath = isSubdomainStoreMode()
        ? buildCustomerRoute(null, '')
        : (currentStoreId ? buildCustomerRoute(currentStoreId, '') : '/not-found');
      return <Navigate to={redirectPath} replace />;
    } else {
      return <Navigate to="/management/appointments" replace />;
    }
  }

  return children;
}

